import React, { Component, Fragment } from 'react';
import { connect } from "react-redux";
import DispatchProps from "../../../../utils/DispatchProps";
import { IRootState } from "../../../../store";
import ApiService from "../../../ApiService";
import { GroundFloorDurableFunctionInstance } from "../../../../models/DurableFunctionInstance";
import { unfriendlyDurableFunctionInstanceName } from "../../../../utils/Utils";
import { WorkUnitInvocationLog } from "../../../../models/WorkUnitInvocationLog";
import WorkUnitInvocationLogList from "../../../WorkUnitInvocationLogList";
import GroundFloorDurableFunctionInstanceList from "../../../GroundFloorDurableFunctionInstanceList";
import School from "../../../../models/School";
import { buildIndexedSchools } from "../../../../utils/IndexedSchools";
import {AnsiUp} from "ansi_up/ansi_up";
import LoadingCard from "../../../LoadingCard";
import SchoolPageTitle from '../../../SchoolPageTitle';
import AdminSchoolMenu from '../../../AdminSchoolMenu';

interface WorkUnitLogsPageProps {
    workloadInstanceId: string;
    workUnitInvocationId: string;
    apiService: ApiService;
}

interface WorkUnitLogsPageState {
    school?: School;
    workloadInstance?: GroundFloorDurableFunctionInstance;
    workUnitInvocationLog?: WorkUnitInvocationLog;
    logFileContent?: string;
    logFileIsLoading: boolean;
}

class WorkUnitLogsPage extends Component<WorkUnitLogsPageProps & DispatchProps, WorkUnitLogsPageState> {
    static displayName = WorkUnitLogsPage.name;

    constructor(props: WorkUnitLogsPageProps & DispatchProps) {
        super(props);

        this.state = {
            logFileIsLoading: false
        };
    }

    async componentDidMount() {
        if (this.props.workloadInstanceId && this.props.workUnitInvocationId) {
            await this.fetchLogFileContent();
        }

        if (this.props.workloadInstanceId) {
            await this.fetchWorkloadInstanceAndSchool();
        }
    }

    async componentDidUpdate(prevProps: Readonly<WorkUnitLogsPageProps & DispatchProps>, prevState: any): Promise<void> {

        if (this.props.workloadInstanceId !== prevProps.workloadInstanceId) {
            await this.fetchWorkloadInstanceAndSchool();
        }

        if (this.props.workloadInstanceId !== prevProps.workloadInstanceId || this.props.workUnitInvocationId !== prevProps.workUnitInvocationId) {

            await this.fetchLogFileContent();
        }

        if (this.state.workloadInstance !== prevState.workloadInstance) {
            this.setWorkUnitInvocationLog();
        }
    }

    private setWorkUnitInvocationLog(): void {
        if (this.state.workloadInstance) {
            const workUnitInvocationLog = this.state.workloadInstance.workUnitInvocations.filter(l => l.invocationId === this.props.workUnitInvocationId)[0];
            this.setState({
                workUnitInvocationLog
            });
        }
    }

    private async fetchLogFileContent() {
        if (this.props.workloadInstanceId && this.props.workUnitInvocationId) {
            this.setState({
                logFileIsLoading: true
            });

            const logFileContent = await this.props.apiService.getWorkUnitLogs(this.props.workloadInstanceId, this.props.workUnitInvocationId);

            this.setState({
                logFileContent,
                logFileIsLoading: false
            });
        }
    }

    private async fetchWorkloadInstanceAndSchool() {
        const instance = await this.props.apiService.getGroundFloorDurableFunctionInstance(this.props.workloadInstanceId);
        const school = await this.props.apiService.getSchoolAdmin(instance.input.tenantId, instance.input.schoolCode);
        this.setState({
            school: school,
            workloadInstance: instance,
        })
    }

    render() {

        const logFileLink = this.state.workUnitInvocationLog ? <a href={this.state.workUnitInvocationLog.logFileUrl}>{this.state.workUnitInvocationLog.logFileUrl}</a> : undefined;

        const schools = this.state.school ? [this.state.school] : [];
        const indexedSchools = buildIndexedSchools(schools);

        let logsContent: JSX.Element | null  = null;
        if(this.state.logFileIsLoading)
        {
            logsContent = <LoadingCard/>;
        }
        else
        {
            const startsWithAnsiCharCode = this.state.logFileContent?.indexOf("\u001b") === 0;
            if(startsWithAnsiCharCode)
            {
                const ansiUp = new AnsiUp();
                ansiUp.use_classes = true;

                logsContent = (
                    <div className="work-unit-logs" style={{fontFamily: "monospace"}}
                         dangerouslySetInnerHTML={{__html: this.state.logFileContent ? ansiUp.ansi_to_html(this.state.logFileContent) : ""}}>
                    </div>
                );
            }
            else
            {
                logsContent = (
                    <div className="card__content whitespace-pre-wrap" style={{fontFamily: "monospace"}}>
                        {this.state.logFileContent}
                    </div>
                );
            }
        }

        return (
            <Fragment>
                <SchoolPageTitle tenantId={this.state.workloadInstance?.tenantId} schoolCode={this.state.workloadInstance?.schoolCode} title={this.state.workUnitInvocationLog?.workUnitName} />
                <AdminSchoolMenu tenantId={this.state.workloadInstance?.tenantId} schoolCode={this.state.workloadInstance?.schoolCode} />

                <div className={"flex-column"}>

                    <GroundFloorDurableFunctionInstanceList title={this.state.workloadInstance ? unfriendlyDurableFunctionInstanceName(this.state.workloadInstance) : ""} enableRowLinks={false} instances={this.state.workloadInstance ? [this.state.workloadInstance] : []} indexedSchools={indexedSchools} />
                    <WorkUnitInvocationLogList tenantId={this.state.school?.tenantId!} schoolCode={this.state.school?.schoolCode!} title="Work Unit" enableLinks={false} workUnitInvocationLogs={this.state.workUnitInvocationLog ? [this.state.workUnitInvocationLog] : []} />

                    <div className="card flex-item--flex-1">
                        <div className="card__header">
                            <span className={"card__title"}>Logs - {logFileLink}</span>
                        </div>
                        {logsContent}
                    </div>
                </div>
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: any): WorkUnitLogsPageProps => {

        const workloadInstanceId = ownProps.match.params.workloadInstanceId;
        const workUnitInvocationId = ownProps.match.params.workUnitInvocationId;

        return {
            workloadInstanceId,
            workUnitInvocationId,
            apiService: state.auth.apiService,
        };
    }
)(WorkUnitLogsPage);